import { auth0Service, SESSION_EXPIRED_EVENT } from '@shared/lib/auth0-service';

/// Server-side error envelope. Surfaces the `code` and `message` produced by
/// the Rust `ApiError::error_response` so callers can branch on a stable
/// machine-readable identifier instead of parsing English error text.
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /// `403` raised by the locked-fields service when a PATCH attempts to
  /// write a locked column. Distinct from a generic permission denial.
  get isForbidden(): boolean {
    return this.status === 403;
  }

  get isNotFound(): boolean {
    return this.status === 404;
  }
}

async function readError(res: Response, isJson: boolean): Promise<ApiError> {
  let code = 'http_error';
  let message = `HTTP ${res.status}`;
  if (isJson) {
    try {
      const body = (await res.json()) as { error?: { code?: string; message?: string } };
      if (body.error) {
        code = body.error.code ?? code;
        message = body.error.message ?? message;
      }
    } catch {
      // body wasn't valid JSON; keep the defaults
    }
  } else {
    try {
      const text = await res.text();
      if (text.trim().length > 0) message = text;
    } catch {
      // ignore
    }
  }
  return new ApiError(res.status, code, message);
}

/// token 取得失敗を「再認証で解決するか」 判定する predicate の型。
/// 判定ロジック自体は features 層の責務なので shared には置かず、 配線層から
/// `registerReauthPredicate()` で注入する (FSD: shared は判断を持たない)。
export type ReauthPredicate = (err: unknown) => boolean;

/// 注入された再認証要否の判定。 未注入時は「判断しない (= 常に false)」が default。
/// shared 自身は再認証要否を判断せず、 token 失敗エラーをそのまま呼び出し側へ
/// re-throw するに留める。
let reauthPredicate: ReauthPredicate = () => false;

/// features 層 (`features/auth`) の `isReauthRequiredError` を shared に注入する。
/// 配線は app 層 (`app/auth/bootstrap`) から 1 回だけ行う想定。
/// これにより shared → features の逆依存を作らずに、 判断の所有権を features に
/// 置いたまま token 失敗の検知だけを shared で行える。
export function registerReauthPredicate(predicate: ReauthPredicate): void {
  reauthPredicate = predicate;
}

/// Auth0 SPA SDK から access token を取得して `Authorization` ヘッダの値を
/// 組み立てる。 SDK 内部で in-memory にキャッシュしているのでリクエスト毎に
/// 呼んでも実際のネットワーク往復は最小限。
///
/// access token の透過更新に失敗し再ログインが必要な場合 (refresh token 失効等)
/// は、 `SESSION_EXPIRED_EVENT` を発火して features 層に再認証フローを委ね、
/// **解決しない Promise を返して** 後続の fetch とエラー表示を抑止する
/// (`store.svelte.ts` の loginWithRedirect 直後と同パターン)。
/// それ以外のエラーは呼び出し側のエラーハンドリングへ re-throw する。
///
/// 「再認証が必要なエラーか」 の判断は features 層の責務であり、 ここでは
/// `registerReauthPredicate()` で注入された predicate に委ねる (shared は判断を
/// 持たずイベント発行のみ)。
async function getBearerHeader(): Promise<string> {
  try {
    const token = await auth0Service.getToken();
    return `Bearer ${token}`;
  } catch (err) {
    if (reauthPredicate(err)) {
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      return new Promise<never>(() => undefined);
    }
    throw err;
  }
}

async function requestJson<T>(input: RequestInfo, init: RequestInit = {}): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: await getBearerHeader(),
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body !== undefined && headers['Content-Type'] === undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(input, { ...init, headers });
  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  if (!res.ok) throw await readError(res, isJson);
  // 成功 + 空 body を許容する。例: backend の create_invitation が
  // `HttpResponse::Created().finish()` で 201 + 空 body を返すケース。
  // 呼び出し側が `Promise<void>` で受けている前提で `undefined` を返す。
  // backend 側で Invitation DTO を返す方が本来正しい設計のため、
  // 別途 #213 で根本対応する予定。
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (text.length === 0) return undefined as T;
  if (!isJson) {
    throw new ApiError(res.status, 'unexpected_content_type', `expected JSON, got ${contentType}`);
  }
  return JSON.parse(text) as T;
}

async function requestNoContent(input: RequestInfo, init: RequestInit = {}): Promise<void> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
    Authorization: await getBearerHeader(),
    ...((init.headers as Record<string, string>) ?? {}),
  };
  if (init.body !== undefined && headers['Content-Type'] === undefined) {
    headers['Content-Type'] = 'application/json';
  }
  const res = await fetch(input, { ...init, headers });
  if (res.status === 204) return;
  const contentType = res.headers.get('content-type') ?? '';
  const isJson = contentType.includes('application/json');
  if (!res.ok) throw await readError(res, isJson);
  // Some endpoints (e.g. POST) succeed with a body — the JSON variant should
  // be used for those. Reaching here means the caller picked the wrong helper.
  throw new ApiError(
    res.status,
    'unexpected_response_body',
    `expected 204 No Content, got HTTP ${res.status}`,
  );
}

export const api = {
  get: <T>(url: string) => requestJson<T>(url),
  post: <T>(url: string, body: unknown) =>
    requestJson<T>(url, { method: 'POST', body: JSON.stringify(body) }),
  patch: <T>(url: string, body: unknown) =>
    requestJson<T>(url, { method: 'PATCH', body: JSON.stringify(body) }),
  put: <T>(url: string, body: unknown) =>
    requestJson<T>(url, { method: 'PUT', body: JSON.stringify(body) }),
  deleteJson: <T>(url: string) => requestJson<T>(url, { method: 'DELETE' }),
  delete: (url: string) => requestNoContent(url, { method: 'DELETE' }),
};
